export type SortDirection = 'ASC' | 'DESC' | 'NONE';

export interface SortState {
    key: string;
    direction: SortDirection;
}

// Helper to generate rows for Provider Location DataTable with expandable rows
function generateProviderLocationExpandableRows(data: any[]): any[] {
    // Group by rosterId
    const grouped = data.reduce((acc: Record<string, any[]>, obj: any) => {
        if (!acc[obj.rosterId]) acc[obj.rosterId] = [];
        acc[obj.rosterId].push(obj);
        return acc;
    }, {});

    // Sort each group by revisionId
    Object.keys(grouped).forEach(rosterId => {
        grouped[rosterId].sort((a: any, b: any) => Number(a.revisionId) - Number(b.revisionId));
    });

    // Each group (rosterId) will have a parent row and its children as expanded rows
    const rows: any[] = [];
    Object.keys(grouped).forEach((rosterId) => {
        const group = grouped[rosterId];
        // Find the child with the latest revisionDateModified
        let latest = group[0];
        group.forEach(item => {
            if (
                item.revisionDateModified &&
                (!latest.revisionDateModified || new Date(item.revisionDateModified) > new Date(latest.revisionDateModified))
            ) {
                latest = item;
            }
        });
        rows.push({
            id: `parent-${rosterId}`,
            isParent: true,
            ...group[0],
            lastModified: latest.revisionDateModified || '-',
            modifiedBy: latest.revisionModifiedBy || '-',
            children: group.map((item) => ({
                id: `child-${rosterId}-${item.revisionId}`,
                isParent: false,
                ...item,
            })),
        });
    });
    return rows;
}

// Helper to sort parent and child rows by revisionDateModified of Provider Location DataTable
export const getProviderLocationSortedRowsWithExpand = (data: any[], sortState: SortState) => {
    let expandableRows = generateProviderLocationExpandableRows(data);
    if (sortState.key === 'revisionDateModified' && sortState.direction !== 'NONE') {
        // Sort parent rows
        expandableRows = expandableRows.sort((a: any, b: any) => {
            const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
            const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
            return sortState.direction === 'ASC' ? dateA - dateB : dateB - dateA;
        });
        // Sort children in each parent
        expandableRows.forEach((parent: any) => {
            parent.children = parent.children.sort((a: any, b: any) => {
                const dateA = a.revisionDateModified ? new Date(a.revisionDateModified).getTime() : 0;
                const dateB = b.revisionDateModified ? new Date(b.revisionDateModified).getTime() : 0;
                return sortState.direction === 'ASC' ? dateA - dateB : dateB - dateA;
            });
        });
    }
    return expandableRows;
};

// Helper to sort rows by revisionDateModified of Member Location DataTable
export const getMemberLocationSortedRowsWithExpand = (data: any[], sortState: SortState) => {
    let sortedRows = [...data];
    if (sortState.key === 'revisionDateModified' && sortState.direction !== 'NONE') {
        sortedRows = sortedRows.sort((a: any, b: any) => {
            const dateA = a.revisionDateModified ? new Date(a.revisionDateModified).getTime() : 0;
            const dateB = b.revisionDateModified ? new Date(b.revisionDateModified).getTime() : 0;
            return sortState.direction === 'ASC' ? dateA - dateB : dateB - dateA;
        });
    }
    return sortedRows;
};